/**
 * EditorStyleContext — provee acceso a los estilos por campo (FieldStyle) que
 * persisten en `PlanSection.styles[fieldPath]`. Las secciones consumen el
 * context para leer/escribir styles sin tener que recibir props nuevos en
 * cascada.
 *
 * Cuando el `<TextStylePopover>` aplica un cambio, llama `setFieldStyle(...)`
 * que muta el PlanDoc del padre (PlanBuilderScreen) y dispara autosave.
 *
 * Modo readonly (vista pública / preview de plantilla): los estilos se leen
 * pero los setters son no-op.
 */
import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { FieldStyle } from "../../types/plan";

export interface EditorStyleValue {
  /** Lee el style aplicado a un campo. Devuelve undefined si no hay. */
  getFieldStyle: (sectionId: string, fieldPath: string) => FieldStyle | undefined;
  /** Reemplaza el style completo del campo (o lo borra si pasa undefined/{}). */
  setFieldStyle: (sectionId: string, fieldPath: string, style: FieldStyle | undefined) => void;
  /** Aplica un patch parcial mergeando con el style existente. */
  patchFieldStyle: (sectionId: string, fieldPath: string, patch: Partial<FieldStyle>) => void;
  /** Limpia todos los styles del campo. */
  clearFieldStyle: (sectionId: string, fieldPath: string) => void;
  /** Modo solo-lectura (vistas públicas). */
  readonly: boolean;
}

const EditorStyleContext = createContext<EditorStyleValue | null>(null);

interface ProviderProps {
  value: EditorStyleValue;
  children: ReactNode;
}

export function EditorStyleProvider({ value, children }: ProviderProps) {
  return <EditorStyleContext.Provider value={value}>{children}</EditorStyleContext.Provider>;
}

/** Hook que devuelve el context activo o un stub no-op si no está mounted. */
export function useEditorStyle(): EditorStyleValue {
  const ctx = useContext(EditorStyleContext);
  return ctx || STUB;
}

const STUB: EditorStyleValue = {
  getFieldStyle: () => undefined,
  setFieldStyle: () => {},
  patchFieldStyle: () => {},
  clearFieldStyle: () => {},
  readonly: true,
};

/**
 * Convierte un FieldStyle en CSSProperties para aplicar inline al span.
 */
export function fieldStyleToCss(style: FieldStyle | undefined): React.CSSProperties {
  if (!style) return {};
  const css: React.CSSProperties = {};
  if (style.fontFamily) css.fontFamily = style.fontFamily;
  if (style.fontWeight) (css as any).fontWeight = style.fontWeight;
  if (style.fontStyle) css.fontStyle = style.fontStyle as any;
  if (style.textDecoration) css.textDecoration = style.textDecoration;
  if (style.textAlign) css.textAlign = style.textAlign as any;
  if (style.fontSize) css.fontSize = style.fontSize;
  if (style.color) css.color = style.color;
  return css;
}

/**
 * Hook que usa el InlineEditable para resolver el style efectivo de su campo.
 * Si no se proveen sectionId/fieldPath, devuelve undefined (sin estilos
 * persistidos — comportamiento default).
 */
export function useFieldStyle(
  sectionId: string | undefined,
  fieldPath: string | undefined,
): FieldStyle | undefined {
  const ctx = useEditorStyle();
  return useMemo(() => {
    if (!sectionId || !fieldPath) return undefined;
    return ctx.getFieldStyle(sectionId, fieldPath);
  }, [ctx, sectionId, fieldPath]);
}

// ----------------------------------------------------------------------
// SectionIdContext — propaga el id de la sección activa a todos los
// `<InlineEditable>` que estén dentro de una sección sin tener que pasar
// `sectionId` por prop en cada llamada. SectionView envuelve cada
// subsección con este Provider.
// ----------------------------------------------------------------------

import { createContext as createCtx } from "react";

export const SectionIdContext = createCtx<string | null>(null);

export function useCurrentSectionId(): string | null {
  return useContext(SectionIdContext);
}
